import { useState } from "react";
import { Link } from "react-router-dom";
import { Palette, Quote, Download, Loader2, Sparkles, ArrowLeft, Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteColoringGenerator = () => {
  const [quote, setQuote] = useState("");
  const [style, setStyle] = useState("inspirational");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generationsLeft, setGenerationsLeft] = useState(3);

  const styles = [
    { id: "inspirational", label: "Inspirational", desc: "Uplifting with decorative borders" },
    { id: "floral", label: "Floral Frame", desc: "Surrounded by flowers" },
    { id: "mandala", label: "Mandala Style", desc: "With intricate patterns" },
    { id: "minimalist", label: "Minimalist", desc: "Clean and simple" }
  ];

  const sampleQuotes = [
    "Be the change you wish to see",
    "Dream big, work hard",
    "Believe in yourself",
    "Choose happiness",
    "Stay positive"
  ];

  const handleGenerate = async () => {
    if (!quote.trim()) {
      toast.error("Please enter a quote");
      return;
    }

    if (generationsLeft <= 0) {
      toast.error("You've used all free generations. Sign up for more!");
      return;
    }

    setIsGenerating(true);
    
    try {
      let prompt = "";
      switch (style) {
        case "inspirational":
          prompt = `Coloring page with inspirational quote "${quote}" in beautiful decorative lettering, surrounded by stars and swirls, ornamental border, black and white line art for adult coloring`;
          break;
        case "floral":
          prompt = `Coloring page with quote "${quote}" in elegant script, framed by detailed flowers, roses and leaves border, black and white line art for coloring`;
          break;
        case "mandala":
          prompt = `Coloring page with quote "${quote}" in the center, surrounded by intricate mandala patterns, geometric and floral designs, black and white line art for adult coloring`;
          break;
        case "minimalist":
          prompt = `Coloring page with quote "${quote}" in modern minimalist calligraphy style, simple elegant design with subtle decorations, black and white line art for coloring`;
          break;
        default:
          prompt = `Coloring page with quote "${quote}" in decorative lettering, black and white line art for coloring`;
      }

      const response = await axios.post(`${BACKEND_URL}/api/generate/free-tool`, {
        prompt: prompt,
        tool_type: "quote_generator"
      });

      if (response.data.image_url) {
        setGeneratedImage(response.data.image_url);
        setGenerationsLeft(prev => prev - 1);
        toast.success("Quote coloring page generated!");
      }
    } catch (error) {
      console.error("Generation error:", error);
      if (error.response?.status === 429) {
        toast.error("Rate limit reached. Please try again later or sign up!");
      } else {
        toast.error("Failed to generate. Please try again.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (generatedImage) {
      const link = document.createElement("a");
      link.href = generatedImage;
      link.download = `quote-coloring-page.png`;
      link.click();
      toast.success("Download started!");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/free-tools" className="text-slate-600 hover:text-slate-900 font-medium hidden md:flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                All Tools
              </Link>
              <Button
                onClick={() => window.location.href = "/signup"}
                className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <Link to="/free-tools" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4">
              <ArrowLeft className="w-4 h-4" />
              Back to Free Tools
            </Link>
            
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full mb-4">
              <Quote className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Free Quote Generator</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Quote Coloring Page Generator
            </h1>
            
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Turn your favorite quotes into beautiful coloring pages. Perfect for 
              mindfulness, stress relief, or inspirational decor!
            </p>
          </div>

          {/* Generator Card */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-6">Create Your Quote Page</h2>
              
              {/* Quote Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Enter Your Quote
                </label>
                <textarea
                  value={quote}
                  onChange={(e) => setQuote(e.target.value)}
                  placeholder="e.g., Be the change you wish to see in the world"
                  maxLength={100}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all resize-none"
                  data-testid="quote-input"
                />
                <p className="text-xs text-slate-500 mt-1">{quote.length}/100 characters</p>
              </div>

              {/* Sample Quotes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Or try a sample:
                </label>
                <div className="flex flex-wrap gap-2">
                  {sampleQuotes.map((sq, i) => (
                    <button
                      key={i}
                      onClick={() => setQuote(sq)}
                      className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-blue-100 text-slate-600 hover:text-blue-700 rounded-full transition-colors"
                    >
                      {sq}
                    </button>
                  ))}
                </div>
              </div>

              {/* Style Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Choose Style
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {styles.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStyle(s.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${
                        style === s.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-blue-200"
                      }`}
                      data-testid={`style-${s.id}`}
                    >
                      <div className="font-medium text-slate-800 text-sm">{s.label}</div>
                      <div className="text-xs text-slate-500">{s.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || !quote.trim()}
                className="w-full h-14 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold text-lg disabled:opacity-50"
                data-testid="generate-btn"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Your Quote...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Quote Page
                  </>
                )}
              </Button>

              {/* Generations Counter */}
              <div className="mt-4 text-center">
                <span className="text-sm text-slate-500">
                  {generationsLeft} free generations remaining
                </span>
                {generationsLeft <= 1 && (
                  <Link to="/signup" className="block text-blue-600 text-sm font-medium mt-1 hover:underline">
                    Sign up for unlimited generations →
                  </Link>
                )}
              </div>
            </div>

            {/* Preview Section */}
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-6">Preview</h2>
              
              <div className="aspect-square rounded-xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden">
                {generatedImage ? (
                  <img
                    src={generatedImage}
                    alt="Quote coloring page"
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center p-8">
                    <Quote className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Your quote page will appear here</p>
                    <p className="text-sm text-slate-400 mt-2">Enter a quote and click generate</p>
                  </div>
                )}
              </div>

              {/* Download Button */}
              {generatedImage && (
                <Button
                  onClick={handleDownload}
                  className="w-full h-12 mt-4 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold"
                  data-testid="download-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Free
                </Button>
              )}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Perfect For
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Stress Relief", desc: "Coloring quotes is meditative and calming - perfect for mindfulness practice." },
                { title: "Home Decor", desc: "Frame your colored quotes for inspirational wall art." },
                { title: "Gift Making", desc: "Create personalized, meaningful gifts with custom quotes." }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <Check className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-8 md:p-12 text-center text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Create Unlimited Custom Coloring Pages
            </h2>
            <p className="text-blue-100 mb-6 max-w-xl mx-auto">
              Love quote coloring pages? With ColorWonder, create any coloring page 
              you can imagine - mandalas, animals, scenes, and more!
            </p>
            <Button
              onClick={() => window.location.href = "/signup"}
              className="h-14 px-8 rounded-full bg-white text-blue-600 font-semibold text-lg hover:bg-blue-50"
            >
              Try Full Generator Free →
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8 px-6 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">© 2025 ColorWonder. All rights reserved.</p>
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <Link to="/free-tools" className="hover:text-slate-700">Free Tools</Link>
            <Link to="/blog" className="hover:text-slate-700">Blog</Link>
            <Link to="/privacy" className="hover:text-slate-700">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default QuoteColoringGenerator;
